// Порт ref DesignBlockEditor.tsx:426-494. Отличие от рефа: файл грузит наш CDN-загрузчик
// (бек не принимает data:-URL из <input type="file">) — пустая картинка в канвасе
// рисует тот же `ImageUpload`, что и панель (`CanvasImageUpload`).
// `OptimizedImage` не прокидывает произвольные атрибуты (role) — используем `<img>` напрямую,
// чтобы тест мог найти картинку через `getByRole('presentation')`.
import { Image as ImageIcon } from 'lucide-react'
import { blockTheme, hsl } from '@teach/ui'
import type { DesignImage } from '@/types/blocks'
import { httpsMediaSrc } from '@/features/editor/lib/designUrl'
import { useStrictLang } from './strictLangContext'
import { getLocalizedDisplay } from '@/features/editor/lib/localizedField'
import type { DesignElementProps } from './DesignElementView'
import { CanvasImageUpload } from './CanvasImageUpload'
import { useIsPreviewThumbnail } from '../thumbnailContext'
import { CanvasImageFilled } from './CanvasImageFilled'
import { ImageWithSkeleton } from '@/features/editor/components/shared/ImageWithSkeleton'

export const ImageElement = ({
  element,
  editable,
  contentLang,
  isRun,
  onUpdate,
}: DesignElementProps<DesignImage>) => {
  const isThumbnail = useIsPreviewThumbnail()
  // Симметричный фолбэк только для показа (read-only): в правке пустой перевод должен
  // остаться пустым, иначе панель (без фолбэка) и канвас разойдутся во мнениях.
  const strictLang = useStrictLang()
  const url = getLocalizedDisplay(element, 'imageUrl', contentLang, editable || strictLang)
  // Политика ссылок та же, что у кастомной иконки бейджа (designUrl): не-https бек
  // в дизайн-блоке не принимает и обнуляет на чтении, так что нарисованная картинка
  // исчезла бы после первого сохранения. Отклонённая ссылка = «картинки нет» (ниже).
  // Руками URL не вводят: его отдаёт наш CDN через ImageUpload в панели. ImageUpload —
  // общий на все типы блоков, где это правило не действует, поэтому свою превьюшку
  // он не фильтрует: если CDN когда-нибудь отдаст не-https, панель покажет картинку,
  // а канвас — пустую рамку. Расхождение заметное и чинится в ImageUpload, не здесь.
  const src = url ? httpsMediaSrc(url) : null

  if (src) {
    // Не выше, чем шире (максимум 1:1): вертикальная картинка обрезается по центру,
    // как в плеерах (PRD #71, п.19).
    // `cqw` считается от ширины обёртки — `container-type` на ней.
    const frame = (
      <div className="w-full overflow-visible" style={{ containerType: 'inline-size' }}>
        <div
          data-testid="design-image-frame"
          className="relative w-full overflow-hidden rounded-lg transition-transform duration-300"
          style={{
            transform: element.imageRotation ? `rotate(${element.imageRotation}deg)` : undefined,
          }}
        >
          <ImageWithSkeleton
            src={src}
            role="presentation"
            className="block max-h-[100cqw] w-full object-cover object-center"
            skeletonClassName="aspect-square w-full"
            skeletonStyle={{ backgroundColor: hsl(blockTheme.muted) }}
          />
        </div>
      </div>
    )
    return editable && url ? (
      <CanvasImageFilled element={element} contentLang={contentLang} url={url} onUpdate={onUpdate}>
        {frame}
      </CanvasImageFilled>
    ) : (
      frame
    )
  }

  // Прохождение: плеер академии на пустом URL рисует `null` — серый плейсхолдер
  // ниже это авторский аффорданс формы, ученик его не увидит никогда.
  // Миниатюра в ленте экранов: пустую картинку не рисуем вовсе — серая плашка
  // там только шумит (на холсте вместо неё зона загрузки).
  if (isRun || isThumbnail) return null

  return editable ? (
    // Тот же модуль загрузки, что в панели справа: нажать или перетащить файл.
    <CanvasImageUpload element={element} contentLang={contentLang} onUpdate={onUpdate} />
  ) : (
    <div
      data-testid="design-image-empty"
      className="flex aspect-[3/1] min-h-[60px] w-full items-center justify-center rounded-lg"
      style={{ backgroundColor: hsl(blockTheme.muted) }}
    >
      <ImageIcon className="h-8 w-8" style={{ color: hsl(blockTheme.foreground, 0.3) }} />
    </div>
  )
}
